import { useEffect, useState } from 'react';
import type { Account, AccountInput, ProviderPreset, Security, TestResult } from '@momomail/shared';
import { api, errorText } from '../lib/bridge';
import { t, tMessage } from '../lib/i18n';
import { startGoogleFlow, useGoogleInfo } from './Google';
import { Icon } from './Icon';
import { toast } from './Toasts';

const COLORS = ['#5b3df5', '#ff4f6d', '#1fc98f', '#17b6d6', '#f5b800', '#ff8a3d', '#c04bd8', '#5d5987'];
const SECURITY = (): { value: Security; label: string }[] => [
  { value: 'tls', label: 'SSL/TLS' },
  { value: 'starttls', label: 'STARTTLS' },
  { value: 'none', label: t('不加密') },
];

/** Small badge on the provider card: an initial-letter sticker. */
function ProviderBadge({ preset }: { preset: ProviderPreset }) {
  const letter: Record<string, string> = { gmail: 'G', outlook: 'O', qq: 'Q', '163': '网', icloud: 'i', custom: '@' }; // i18n-ok badge letters
  return (
    <span className="provider__badge" style={{ background: preset.color }}>
      {letter[preset.id] ?? '@'}
    </span>
  );
}

export function ProviderPicker({ presets, onPick }: { presets: ProviderPreset[]; onPick: (p: ProviderPreset) => void }) {
  return (
    <div className="providers">
      {presets.map((p) => (
        <button key={p.id} className="provider" onClick={() => onPick(p)}>
          <ProviderBadge preset={p} />
          <span className="provider__label">{tMessage(p.label)}</span>
          {p.id !== 'custom' && <span className="provider__hint">{tMessage(p.passwordLabel)}</span>}
        </button>
      ))}
    </div>
  );
}

function blankInput(preset: ProviderPreset, color: string): AccountInput {
  return {
    name: '',
    email: '',
    displayName: '',
    color,
    provider: preset.id,
    imap: { ...preset.imap },
    smtp: { ...preset.smtp },
    username: '',
    password: '',
    authKind: 'password',
    signature: '',
  };
}

/**
 * Add / edit an account. Picking a provider auto-fills the servers, kept under "服务器设置" (server settings);
 * before saving, test that IMAP and SMTP can log in; on failure you can edit or "仍然保存" (save anyway).
 */
export function AccountForm({ presets, preset: initialPreset, account, onCancel, onSaved, compact }: { presets: ProviderPreset[]; preset: ProviderPreset; account: Account | null; onCancel: () => void; onSaved: (a: Account) => void; compact?: boolean }) {
  const [preset, setPreset] = useState(initialPreset);
  const [input, setInput] = useState<AccountInput>(() =>
    account
      ? { id: account.id, name: account.name, email: account.email, displayName: account.displayName, color: account.color, provider: account.provider, imap: account.imap, smtp: account.smtp, username: account.username, password: '', authKind: account.authKind, signature: account.signature }
      : blankInput(initialPreset, COLORS[Math.floor(Math.random() * 4)]!),
  );
  const [advanced, setAdvanced] = useState(initialPreset.id === 'custom' && !account);
  const [usernameTouched, setUsernameTouched] = useState(!!account);
  const [busy, setBusy] = useState<'test' | 'save' | null>(null);
  const [test, setTest] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setTest(null), [input.imap, input.smtp, input.username, input.password]);

  // Gmail: when the server has Google sign-in configured, choose "用 Google 登录" (sign in with Google) or an app password
  const google = useGoogleInfo();
  const canGoogle = preset.oauth === 'google' && !!google?.google;
  const [method, setMethod] = useState<'password' | 'google'>('password');
  const [googleBusy, setGoogleBusy] = useState(false);

  const googleSignIn = async (accountId?: string) => {
    setGoogleBusy(true);
    setError(null);
    try {
      const state = await startGoogleFlow({ purpose: 'mail', ...(accountId ? { accountId } : {}), ...(input.email.trim() ? { loginHint: input.email.trim() } : {}) });
      if (state?.status !== 'done') return;
      const saved = (await api().accounts.list()).find((a) => a.id === state.accountId);
      if (saved) onSaved(saved);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setGoogleBusy(false);
    }
  };

  const set = (patch: Partial<AccountInput>) => {
    setError(null);
    setInput((i) => ({ ...i, ...patch }));
  };

  const onEmail = (email: string) => {
    const patch: Partial<AccountInput> = { email };
    if (!usernameTouched) patch.username = email.trim();
    // Once an email is entered, detect the provider too (only under "其他" (other), or when the servers haven't been touched yet)
    const domain = email.split('@')[1]?.toLowerCase();
    if (!account && domain) {
      const found = presets.find((p) => p.domains.includes(domain));
      if (found && found.id !== preset.id) {
        setPreset(found);
        const host = (h: string) => (found.id === '163' && domain !== '163.com' ? h.replace('163.com', domain) : h);
        patch.provider = found.id;
        patch.imap = { ...found.imap, host: host(found.imap.host) };
        patch.smtp = { ...found.smtp, host: host(found.smtp.host) };
      }
    }
    set(patch);
  };

  const runTest = async (): Promise<TestResult | null> => {
    setBusy('test');
    setError(null);
    try {
      const r = await api().accounts.test(input);
      setTest(r);
      return r;
    } catch (e) {
      setError(errorText(e));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const save = async (force = false) => {
    if (!input.email.trim()) return setError(t('请填写邮箱地址'));
    if (!account && !input.password) return setError(t('请填写{what}', { what: tMessage(preset.passwordLabel) }));
    if (!force) {
      const r = test ?? (await runTest());
      if (!r) return;
      if (!r.imap.ok || !r.smtp.ok) return;
    }
    setBusy('save');
    try {
      const saved = await api().accounts.save({ ...input, name: input.name.trim() || input.email.split('@')[0] || input.email, displayName: input.displayName.trim() || input.email.split('@')[0] || '' });
      toast(account ? t('账户已更新') : t('已添加 {email}，正在收信…', { email: saved.email }), 'ok');
      onSaved(saved);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const failed = test && (!test.imap.ok || !test.smtp.ok);

  return (
    <form
      className={`account-form ${compact ? 'account-form--compact' : ''}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (canGoogle && !account && method === 'google') void googleSignIn();
        else void save();
      }}
    >
      <div className="account-form__provider">
        <ProviderBadge preset={preset} />
        <div>
          <strong>{tMessage(preset.label)}</strong>
          <p className="muted">{tMessage(preset.note)}</p>
        </div>
      </div>

      {canGoogle && !account && (
        <div className="signin-choice">
          <div className="segmented" role="group" aria-label={t('登录方式')}>
            <button type="button" aria-pressed={method === 'password'} onClick={() => setMethod('password')}>
              {t('应用专用密码（推荐，最稳定）')}
            </button>
            <button type="button" aria-pressed={method === 'google'} onClick={() => setMethod('google')}>
              {t('用 Google 登录')}
            </button>
          </div>
          <p className="muted signin-choice__note">
            {method === 'google'
              ? t('Google 登录最省事，但 Google 那边有变动时（改密码、撤销授权）要重新登录一次。')
              : t('应用专用密码一次设置、长期有效，不受 Google 授权变动影响。学校、公司的账号可能不支持。')}
          </p>
        </div>
      )}

      {canGoogle && !account && method === 'google' ? (
        <div className="signin-google">
          <label className="field">
            <span className="field__label">{t('Gmail 地址（可以不填，在 Google 页面上选）')}</span>
            <input type="email" value={input.email} onChange={(e) => onEmail(e.target.value)} placeholder="name@gmail.com" autoFocus />
          </label>
          {error && (
            <div className="notice notice--error" role="alert">
              {error}
            </div>
          )}
          <div className="account-form__actions">
            <button type="button" className="btn btn--quiet" onClick={onCancel}>
              {t('返回')}
            </button>
            <span className="compose__spacer" />
            <button type="button" className="btn btn--primary" disabled={googleBusy} onClick={() => void googleSignIn()}>
              {googleBusy ? <span className="spinner" /> : <span className="google-g" aria-hidden>G</span>}
              {t('用 Google 登录')}
            </button>
          </div>
        </div>
      ) : (
      <>
      <div className="form-grid">
        <label className="field">
          <span className="field__label">{t('邮箱地址')}</span>
          <input type="email" value={input.email} onChange={(e) => onEmail(e.target.value)} placeholder="name@example.com" autoFocus={!account} disabled={!!account} required />
        </label>
        {input.authKind === 'oauth2' ? (
          <div className="field">
            <span className="field__label">{t('登录方式')}</span>
            {canGoogle && account ? (
              <div>
                <p className="muted">
                  <Icon name="lock" size={13} /> {t('用 Google 登录（OAuth 授权）。')}
                </p>
                <button type="button" className="btn btn--small" disabled={googleBusy} onClick={() => void googleSignIn(account.id)}>
                  {googleBusy ? <span className="spinner" /> : <span className="google-g" aria-hidden>G</span>} {t('重新用 Google 登录')}
                </button>
              </div>
            ) : (
              <p className="muted">
                <Icon name="lock" size={13} /> {t('Google OAuth 授权。需要重新授权时，在服务器上运行')} <code>account reauth {input.email}</code>
              </p>
            )}
          </div>
        ) : (
          <label className="field">
            <span className="field__label">
              {tMessage(preset.passwordLabel)}
              {account && <span className="muted">{t('（留空不修改）')}</span>}
            </span>
            <input type="password" value={input.password ?? ''} onChange={(e) => set({ password: e.target.value })} placeholder={account ? '••••••••' : tMessage(preset.passwordLabel)} autoComplete="new-password" />
            {preset.helpUrl && (
              <button type="button" className="field__link" onClick={() => void api().mail.openExternal(preset.helpUrl!)}>
                <Icon name="external" size={13} /> {t('怎么获取{what}？', { what: tMessage(preset.passwordLabel) })}
              </button>
            )}
          </label>
        )}
        <label className="field">
          <span className="field__label">{t('账户名称')}</span>
          <input value={input.name} onChange={(e) => set({ name: e.target.value })} placeholder={t('比如：工作、个人')} />
        </label>
        <label className="field">
          <span className="field__label">{t('发信时显示的名字')}</span>
          <input value={input.displayName} onChange={(e) => set({ displayName: e.target.value })} placeholder={t('对方看到的名字')} />
        </label>
      </div>

      <div className="field">
        <span className="field__label">{t('颜色')}</span>
        <div className="swatches" role="radiogroup" aria-label={t('账户颜色')}>
          {COLORS.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={input.color === c} aria-label={c} className="swatch" style={{ background: c }} onClick={() => set({ color: c })} />
          ))}
        </div>
      </div>

      <label className="field">
        <span className="field__label">{t('签名')}</span>
        <textarea rows={2} value={input.signature} onChange={(e) => set({ signature: e.target.value })} placeholder={t('写信时自动加在末尾（可以不填）')} />
      </label>

      <details className="advanced" open={advanced} onToggle={(e) => setAdvanced((e.target as HTMLDetailsElement).open)}>
        <summary>
          <Icon name="sliders" size={15} /> {t('服务器设置')}
        </summary>
        <div className="servers">
          {(['imap', 'smtp'] as const).map((kind) => (
            <fieldset key={kind} className="server">
              <legend>{kind === 'imap' ? t('收信（IMAP）') : t('发信（SMTP）')}</legend>
              <label className="field server__host">
                <span className="field__label">{t('服务器')}</span>
                <input value={input[kind].host} onChange={(e) => set({ [kind]: { ...input[kind], host: e.target.value } })} placeholder={kind === 'imap' ? 'imap.example.com' : 'smtp.example.com'} />
              </label>
              <label className="field server__port">
                <span className="field__label">{t('端口')}</span>
                <input inputMode="numeric" value={String(input[kind].port || '')} onChange={(e) => set({ [kind]: { ...input[kind], port: Number(e.target.value.replace(/\D/g, '')) || 0 } })} />
              </label>
              <label className="field server__sec">
                <span className="field__label">{t('加密')}</span>
                <select
                  value={input[kind].security}
                  onChange={(e) => {
                    const security = e.target.value as Security;
                    const port = kind === 'imap' ? (security === 'tls' ? 993 : 143) : security === 'tls' ? 465 : security === 'starttls' ? 587 : 25;
                    set({ [kind]: { ...input[kind], security, port } });
                  }}
                >
                  {SECURITY().map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </fieldset>
          ))}
          <label className="field">
            <span className="field__label">{t('用户名（一般就是邮箱地址）')}</span>
            <input
              value={input.username}
              onChange={(e) => {
                setUsernameTouched(true);
                set({ username: e.target.value });
              }}
            />
          </label>
          {(input.imap.security === 'none' || input.smtp.security === 'none') && (
            <div className="notice notice--warn">
              <Icon name="lock" size={14} /> {t('不加密时密码会明文经过网络，只建议用于本机测试服务器。')}
            </div>
          )}
          <p className="muted account-form__oauth">
            <Icon name="lock" size={13} /> {t('密码只加密保存在后端（本机或你的服务器），不会发给别处。也可以在服务器上用命令行')} <code>account add</code> {t('添加账户，Gmail 支持 OAuth 授权。')}
          </p>
        </div>
      </details>

      {test && (
        <div className={`test-result ${failed ? 'is-bad' : 'is-ok'}`} role="status">
          <div>
            <Icon name={test.imap.ok ? 'check' : 'alert'} size={15} /> {t('收信（IMAP）：')}{test.imap.ok ? t('连接成功') : tMessage(test.imap.error ?? '')}
          </div>
          <div>
            <Icon name={test.smtp.ok ? 'check' : 'alert'} size={15} /> {t('发信（SMTP）：')}{test.smtp.ok ? t('连接成功') : tMessage(test.smtp.error ?? '')}
          </div>
        </div>
      )}
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}

      <div className="account-form__actions">
        <button type="button" className="btn btn--quiet" onClick={onCancel}>
          {account ? t('取消') : t('返回')}
        </button>
        <span className="compose__spacer" />
        <button type="button" className="btn btn--small btn--quiet" onClick={() => void runTest()} disabled={!!busy}>
          {busy === 'test' ? <span className="spinner" /> : <Icon name="zap" size={15} />}
          {t('测试连接')}
        </button>
        {failed && (
          <button type="button" className="btn btn--quiet" onClick={() => void save(true)} disabled={!!busy}>
            {t('仍然保存')}
          </button>
        )}
        <button type="submit" className="btn btn--primary" disabled={!!busy}>
          {busy ? <span className="spinner" /> : <Icon name="check" size={16} />}
          {busy === 'test' ? t('正在测试…') : busy === 'save' ? t('保存中…') : account ? t('保存') : t('测试并添加')}
        </button>
      </div>
      </>
      )}
    </form>
  );
}
